import { doc, getDocFromServer, onSnapshot } from 'firebase/firestore';
import { db } from '.';

export const KEY_RELEASED = 'RELEASED';
export const KEY_WAIT_EXPIRED = 'EXPIRED';
export const KEY_UNREADABLE = 'UNREADABLE';

// server-confirmed reads while the listener is not confirming - before its first server snapshot, after the
// connection drops, or once the listen has failed
const KEY_POLL_INTERVAL_MS = 5000;

/**
 * Waits for an idempotency key to be released - its document deleted - by the server call holding it.
 *
 * The listener is the fast path. Firestore keeps a listener attached through a dropped connection and serves its
 * cache meanwhile, so only a snapshot confirmed by the server is trusted: a cached "does not exist" can simply mean
 * the key was never cached, and acting on it would re-send work that is still running. Whenever the server is not
 * confirming, the document is read from the server on an interval instead, and a failed read keeps waiting - a poor
 * connection is exactly when the wait matters.
 *
 * @param {string} key the key document id, e.g. `consult:<refId>`
 * @param {number} windowMs how long to wait before giving up
 * @returns {Promise<string>} KEY_RELEASED, KEY_WAIT_EXPIRED, or KEY_UNREADABLE when the rules refuse the read
 */
export const waitForIdempotencyKeyRelease = (key, windowMs) => new Promise((resolve) => {
    const ref = doc(db, 'idempotencyKeys', key);
    let isSettled = false;
    let isListenerConfirmed = false;
    let unsubscribe = null;
    let pollTimeoutId = null;
    let windowTimeoutId = null;

    const settle = (outcome) => {
        if (isSettled) {
            return;
        }
        isSettled = true;
        if (unsubscribe !== null) {
            unsubscribe();
            unsubscribe = null;
        }
        clearTimeout(pollTimeoutId);
        clearTimeout(windowTimeoutId);
        resolve(outcome);
    };

    const readFromServer = async () => {
        try {
            const snapshot = await getDocFromServer(ref);
            if (!snapshot.exists()) {
                settle(KEY_RELEASED);
            }
        } catch (error) {
            if (error?.code === 'permission-denied') {
                settle(KEY_UNREADABLE);
            }
        }
    };

    const schedulePoll = () => {
        pollTimeoutId = setTimeout(async () => {
            if (isSettled) {
                return;
            }
            if (!isListenerConfirmed) {
                await readFromServer();
            }
            if (!isSettled) {
                schedulePoll();
            }
        }, KEY_POLL_INTERVAL_MS);
    };

    windowTimeoutId = setTimeout(() => { settle(KEY_WAIT_EXPIRED); }, windowMs);

    unsubscribe = onSnapshot(ref, { includeMetadataChanges: true }, (snapshot) => {
        if (snapshot.metadata.fromCache) {
            isListenerConfirmed = false;
            return;
        }
        isListenerConfirmed = true;
        if (!snapshot.exists()) {
            settle(KEY_RELEASED);
        }
    }, (error) => {
        // a rejected listen is terminal - firestore never retries it - so the interval reads take over
        unsubscribe = null;
        isListenerConfirmed = false;
        if (error?.code === 'permission-denied') {
            settle(KEY_UNREADABLE);
        }
    });

    schedulePoll();
});
